import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

/** Search merchants by name, EIN or MID and attach a record; or create the merchant from the record. */
export function LinkMerchantDialog({ recordType, recordId, linked, onClose, onLinked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      api.request(`/identity/businesses?limit=20${q ? `&q=${encodeURIComponent(q)}` : ''}`).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const link = async (body, ok) => {
    setBusy(true);
    try {
      const r = await api.request('/identity/link', { method: 'POST', body: JSON.stringify({ record_type: recordType, record_id: recordId, ...body }) });
      toast.success(ok);
      onLinked(r.business_id);
    } catch (e) { toast.error(e.message || 'Could not link'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="link-merchant-dialog">
        <DialogHeader>
          <DialogTitle>Link to merchant</DialogTitle>
          <DialogDescription>Attach this {recordType} to the merchant it belongs to so its history, MIDs and residuals line up.</DialogDescription>
        </DialogHeader>
        <Input autoFocus placeholder="Search business name, EIN or MID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-merchant-search" />
        <div className="max-h-64 space-y-1 overflow-y-auto" data-testid="link-merchant-results">
          {results.length === 0 && <p className="py-3 text-sm text-muted-foreground">No matching merchants.</p>}
          {results.map((b) => (
            <button key={b.id} type="button" disabled={busy} onClick={() => link({ business_id: b.id }, `Linked to ${b.legal_name}`)}
              className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-muted" data-testid={`link-merchant-option-${b.id}`}>
              <span><span className="font-medium">{b.legal_name}</span>{b.dba_names?.length ? <span className="ml-1 text-xs text-muted-foreground">DBA {b.dba_names.join(', ')}</span> : null}</span>
              {b.ein_last4 && <span className="text-xs text-muted-foreground">EIN •••{b.ein_last4}</span>}
            </button>))}
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t pt-3">
          <Button variant="outline" disabled={busy} onClick={() => link({ create: true }, 'Merchant created and linked')} data-testid="link-merchant-create">Create merchant from this {recordType}</Button>
          {linked && <Button variant="ghost" className="text-destructive" disabled={busy} onClick={() => link({}, 'Unlinked')} data-testid="link-merchant-unlink">Unlink</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
